import { useState } from "react";
import { Trash2Icon } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";

type MatrixRowSelectionBarProps = {
  selectedCount: number;
  isDeleting: boolean;
  deleteDisabled: boolean;
  onConfirmDelete: () => void;
};

export function MatrixRowSelectionBar({
  selectedCount,
  isDeleting,
  deleteDisabled,
  onConfirmDelete,
}: MatrixRowSelectionBarProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [wasDeleting, setWasDeleting] = useState(isDeleting);

  // Stays open until the delete resolves; a failure closes it and shows in the alert.
  if (wasDeleting !== isDeleting) {
    setWasDeleting(isDeleting);
    if (!isDeleting) setConfirmOpen(false);
  }

  function handleOpenChange(open: boolean) {
    if (!open && isDeleting) return;
    setConfirmOpen(open);
  }

  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">
        {selectedCount} {selectedCount === 1 ? "row" : "rows"} selected
      </span>
      <Button
        variant="outline"
        size="sm"
        className="text-destructive hover:bg-destructive/10 gap-1.5"
        onClick={() => setConfirmOpen(true)}
        disabled={deleteDisabled}
      >
        <Trash2Icon className="size-3.5" />
        Delete selected
      </Button>

      <Dialog open={confirmOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete rows</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedCount}{" "}
              {selectedCount === 1 ? "row" : "rows"}? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={isDeleting || deleteDisabled}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
